/**
 * Wire ↔ form adapters for the Submissions v2 API.
 *
 * Why an adapter layer?
 *   - The server payload is flat (e.g. `account_manager_name` lives at
 *     the top level), but the UI groups fields into sections
 *     (`accountBasics.accountManagerName`).
 *   - A few representations differ: `coverage` enum vs (gl,pl) booleans,
 *     `administrator_duration_months` int vs (duration string,
 *     years|months) UI pair, premium float vs formatted string.
 *
 * Adapters keep the form components ignorant of the wire shape and
 * vice versa. Save flow consumes `toWire*` to produce PATCH/POST
 * bodies, read flow consumes `fromWireSubmission` to seed the form.
 */
import { readDecFields } from "../dec-fields-local";
import type {
    AdditionalInsuredCoverage,
    AdditionalInsuredForm,
    GeminiContactsForm,
    LocationForm,
    NamedInsuredForm,
    RelativityRow,
    SubmissionAdditionalInsuredWire,
    SubmissionContact,
    SubmissionForm,
    SubmissionLocationWire,
    SubmissionNamedInsuredWire,
    SubmissionRelativityWire,
    SubmissionWire,
} from "../../types/submission";

// ─────────────────────── Primitive helpers ────────────────────

/** Server returns numbers; the form consumes/edits strings (so an
 *  empty input stays empty rather than "0"). */
export function numberToInput(n: number | null | undefined): string {
    return n === null || n === undefined ? "" : String(n);
}

/** Form input → `number | null`. Empty string and non-numeric inputs
 *  collapse to null so they round-trip cleanly through PATCH. */
export function inputToNumber(s: string): number | null {
    if (s.trim() === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
}

export function strOrEmpty(v: string | null | undefined): string {
    return v ?? "";
}

export function emptyToNull(v: string): string | null {
    return v.trim() === "" ? null : v;
}

// ─────────────────────────── Dates ────────────────────────────
//
// Form inputs use US-style MM/DD/YYYY. The backend expects ISO
// YYYY-MM-DD (Pydantic `date`). The two helpers below bridge the gap;
// invalid input collapses to null so a half-typed date never hits the
// API.

export function formDateToIso(input: string): string | null {
    const trimmed = input.trim();
    if (trimmed === "") return null;
    // Accept either MM/DD/YYYY (form input) or YYYY-MM-DD (already ISO)
    // so a round-trip through the adapter is idempotent.
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) return trimmed;
    const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!usMatch) return null;
    const [, mm, dd, yyyy] = usMatch;
    const month = Number(mm);
    const day = Number(dd);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

export function isoDateToForm(iso: string | null | undefined): string {
    if (!iso) return "";
    const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return iso; // already in display format or unexpected — surface as-is
    const [, yyyy, mm, dd] = match;
    return `${mm}/${dd}/${yyyy}`;
}

// ──────────────────────── Coverage ⇄ (gl,pl) ───────────────────

export function coverageToFlags(coverage: AdditionalInsuredCoverage | null): { gl: boolean; pl: boolean } {
    if (coverage === "PL/GL") return { gl: true, pl: true };
    if (coverage === "GL") return { gl: true, pl: false };
    if (coverage === "PL") return { gl: false, pl: true };
    return { gl: false, pl: false };
}

export function flagsToCoverage(gl: boolean, pl: boolean): AdditionalInsuredCoverage | null {
    if (gl && pl) return "PL/GL";
    if (gl) return "GL";
    if (pl) return "PL";
    return null;
}

// ────────────────── Administrator duration ⇄ months ────────────

/** Server stores duration as integer months. The form lets the user
 *  enter a number plus a years|months toggle, so we normalise on save
 *  and try to pick a friendlier unit on load (any number divisible by
 *  12 is shown as years). */
function monthsToDurationForm(months: number | null): { administratorDuration: string; administratorDurationUnit: "years" | "months" } {
    if (months === null || months === undefined) {
        return { administratorDuration: "", administratorDurationUnit: "years" };
    }
    if (months > 0 && months % 12 === 0) {
        return { administratorDuration: String(months / 12), administratorDurationUnit: "years" };
    }
    return { administratorDuration: String(months), administratorDurationUnit: "months" };
}

export function durationFormToMonths(form: Pick<GeminiContactsForm, "administratorDuration" | "administratorDurationUnit">): number | null {
    const n = inputToNumber(form.administratorDuration);
    if (n === null) return null;
    return form.administratorDurationUnit === "years" ? Math.round(n * 12) : Math.round(n);
}

// ─────────────────────── fromWire (read flow) ──────────────────

export function fromWireNamedInsured(wire: SubmissionNamedInsuredWire): NamedInsuredForm {
    return {
        id: wire.id,
        entityName: strOrEmpty(wire.entity_name),
        // Free-form: presets + any custom value the user typed.
        relationship: strOrEmpty(wire.relationship),
        retroactiveDate: isoDateToForm(wire.retroactive_date),
    };
}

export function fromWireAdditionalInsured(wire: SubmissionAdditionalInsuredWire): AdditionalInsuredForm {
    const flags = coverageToFlags(wire.coverage);
    return {
        id: wire.id,
        entityName: strOrEmpty(wire.entity_name),
        gl: flags.gl,
        pl: flags.pl,
        retroactiveDate: isoDateToForm(wire.retroactive_date),
    };
}

export function fromWireLocation(wire: SubmissionLocationWire): LocationForm {
    return {
        id: wire.id,
        locationName: strOrEmpty(wire.location_name),
        address: strOrEmpty(wire.address),
        city: strOrEmpty(wire.city),
        state: strOrEmpty(wire.state),
        zip: strOrEmpty(wire.zip_code),
        glRetroactiveDate: isoDateToForm(wire.gl_retroactive_date),
        plRetroactiveDate: isoDateToForm(wire.pl_retroactive_date),
    };
}

function fromWireContact(contact: SubmissionContact | null): GeminiContactsForm {
    if (!contact) {
        return {
            riskManagerName: "",
            riskManagerEmail: "",
            riskManagerPhone: "",
            administratorName: "",
            administratorEmail: "",
            administratorPhone: "",
            administratorDuration: "",
            administratorDurationUnit: "years",
        };
    }
    return {
        riskManagerName: strOrEmpty(contact.risk_manager_name),
        riskManagerEmail: strOrEmpty(contact.risk_manager_email),
        riskManagerPhone: strOrEmpty(contact.risk_manager_phone),
        administratorName: strOrEmpty(contact.administrator_name),
        administratorEmail: strOrEmpty(contact.administrator_email),
        administratorPhone: strOrEmpty(contact.administrator_phone),
        ...monthsToDurationForm(contact.administrator_duration_months),
    };
}

/** Show exactly what the backend returns for the Relativity factors —
 *  the FE never fakes a default. A present value (including 0) is rendered
 *  verbatim; a null/undefined field renders blank. The UW baseline (e.g.
 *  Additional Exposure = 1) is the backend's job to seed — masking it on
 *  the FE hid a wrong backend seed (additional_rate = 0.0). See OR-1253. */
function rateToString(n: number | null | undefined): string {
    return n === null || n === undefined ? "" : String(n);
}

// TEMP (OR-1253): the backend seeds `additional_rate = 0.0` on submission
// create (should be 1.0 per spec). Until the backend default is fixed, show
// the baseline 1 for Additional Exposure when it arrives null or 0. The other
// factors are shown verbatim. Remove this override once the backend seed is
// corrected (escalated to Henry).
function additionalOrBaseline(n: number | null | undefined): string {
    return n === null || n === undefined || n === 0 ? "1" : String(n);
}

export function fromWireRelativity(wire: SubmissionRelativityWire | null): RelativityRow {
    // No relativity row → blank, except Additional Exposure which falls back to
    // the baseline 1 (see TEMP note above). The backend owns the other defaults.
    if (!wire) {
        return { skilled: "", assisted: "", independent: "", additionalExposure: "1" };
    }
    return {
        skilled: rateToString(wire.skilled_rate),
        assisted: rateToString(wire.assisted_rate),
        independent: rateToString(wire.independent_rate),
        additionalExposure: additionalOrBaseline(wire.additional_rate),
    };
}

export function fromWireSubmission(wire: SubmissionWire): SubmissionForm {
    return {
        id: wire.id,
        status: wire.status ?? "Draft",
        assignee: wire.assignee_id,
        approver: wire.approver_id,
        isBorReceived: wire.is_bor_received ?? null,
        dropboxUrl: strOrEmpty(wire.dropbox_url),
        probabilityToBind: strOrEmpty(wire.probability_to_bind),
        updatedAt: strOrEmpty(wire.updated_at),
        accountBasics: {
            title: wire.title,
            fni: strOrEmpty(wire.fni),
            // OR-1455 — absent from the wire until PR #326 deploys, so fall
            // back to the local overlay. Read here rather than at each of the
            // six `fromWireSubmission` call sites; no-ops behind the flag.
            // See `api/dec-fields-local` — delete both with it.
            mailingAddress: strOrEmpty(wire.mailing_address ?? null) || (readDecFields(wire.id).mailingAddress ?? ""),
            businessDescription: strOrEmpty(wire.business_description ?? null) || (readDecFields(wire.id).businessDescription ?? ""),
            type: wire.type ?? "",
            renewalDate: isoDateToForm(wire.renewal_date),
            expirationDate: isoDateToForm(wire.expiration_date),
            submissionDate: isoDateToForm(wire.submission_date),
            expiringPremium: numberToInput(wire.expiring_premium),
            expiringDeductible: numberToInput(wire.expiring_deductible),
            accountManagerName: strOrEmpty(wire.account_manager_name),
            accountManagerEmail: strOrEmpty(wire.account_manager_email),
        },
        // Brokerage section is "belongs-to" the brokerage relation:
        // when there is no `broker_id`, the read-only display fields
        // are empty regardless of any stale snapshot the wire carries.
        // Stale text would otherwise leak through (mock seeds, legacy
        // rows) and contradict the empty selector. When there *is* a
        // `broker_id`, we surface whatever the server has — the
        // BrokerageSection later overwrites it with fresh values from
        // the brokerage row once the picker resolves.
        brokerage: wire.broker_id
            ? {
                  brokerId: wire.broker_id,
                  brokerEmail: strOrEmpty(wire.broker_email),
                  brokerName: strOrEmpty(wire.broker_name),
                  brokerageName: strOrEmpty(wire.brokerage_name),
                  brokerageAddress: strOrEmpty(wire.brokerage_address),
              }
            : { brokerId: "", brokerEmail: "", brokerName: "", brokerageName: "", brokerageAddress: "" },
        geminiContacts: fromWireContact(wire.contact ?? null),
        note: strOrEmpty(wire.note),
        // The POST /v2/submissions/ response can omit nested
        // collections when no rows exist yet — the schema defaults
        // them to [] but the serializer can drop them. Coalesce
        // defensively so the form always sees an array.
        namedInsureds: (wire.named_insureds ?? []).map(fromWireNamedInsured),
        additionalInsureds: (wire.additional_insureds ?? []).map(fromWireAdditionalInsured),
        locations: (wire.locations ?? []).map(fromWireLocation),
        // Schedule: `relativity` comes from the submission's embedded
        // relativity row; `rateUnadjusted` / `rateAdjustedOccupancy` /
        // `stateDistribution` are computed client-side and `facilities`
        // loads through `useFacilities` — all seeded empty here.
        schedule: {
            rateUnadjusted: "",
            rateAdjustedOccupancy: "",
            relativity: fromWireRelativity(wire.relativity),
            facilities: [],
            stateDistribution: [],
        },
    };
}

// ────────────────────── toWire (save flow) ─────────────────────

/** Top-level submission payload (POST/PATCH /v2/submissions/[id]).
 *  All keys are optional so callers can send only the fields they
 *  actually need to update — the backend treats absent keys as
 *  "leave unchanged" on PATCH. */
export interface SubmissionWritePayload {
    title?: string;
    fni?: string | null;
    /** OR-1455 — accepted once PR #326 deploys; harmless before (Pydantic
     *  drops unknown keys). */
    mailing_address?: string | null;
    business_description?: string | null;
    type?: SubmissionWire["type"];
    status?: SubmissionWire["status"];
    renewal_date?: string | null;
    expiration_date?: string | null;
    submission_date?: string | null;
    expiring_premium?: number | null;
    expiring_deductible?: number | null;
    account_manager_name?: string | null;
    account_manager_email?: string | null;
    broker_id?: string | null;
    broker_email?: string | null;
    broker_name?: string | null;
    brokerage_address?: string | null;
    brokerage_name?: string | null;
    note?: string | null;
    probability_to_bind?: string | null;
    assignee_id?: number | null;
    approver_id?: number | null;
    is_bor_received?: boolean | null;
}

/** Build the flat payload the backend expects from the grouped form
 *  shape. Always includes every top-level field — used for the create
 *  POST. Edit flow should pass the diff (see `submissionPatchPayload`). */
export function toWireSubmission(form: SubmissionForm): SubmissionWritePayload {
    return {
        title: form.accountBasics.title,
        fni: emptyToNull(form.accountBasics.fni),
        mailing_address: emptyToNull(form.accountBasics.mailingAddress),
        business_description: emptyToNull(form.accountBasics.businessDescription),
        type: form.accountBasics.type === "" ? null : form.accountBasics.type,
        status: form.status,
        renewal_date: formDateToIso(form.accountBasics.renewalDate),
        expiration_date: formDateToIso(form.accountBasics.expirationDate),
        submission_date: formDateToIso(form.accountBasics.submissionDate),
        expiring_premium: inputToNumber(form.accountBasics.expiringPremium),
        expiring_deductible: inputToNumber(form.accountBasics.expiringDeductible),
        account_manager_name: emptyToNull(form.accountBasics.accountManagerName),
        account_manager_email: emptyToNull(form.accountBasics.accountManagerEmail),
        broker_id: emptyToNull(form.brokerage.brokerId),
        broker_email: emptyToNull(form.brokerage.brokerEmail),
        broker_name: emptyToNull(form.brokerage.brokerName),
        brokerage_address: emptyToNull(form.brokerage.brokerageAddress),
        brokerage_name: emptyToNull(form.brokerage.brokerageName),
        note: emptyToNull(form.note),
        probability_to_bind: emptyToNull(form.probabilityToBind),
        assignee_id: form.assignee,
        approver_id: form.approver,
    };
}

// Child collection write payloads --------------------------------------------

export interface NamedInsuredWritePayload {
    entity_name: string | null;
    relationship: string | null;
    retroactive_date: string | null;
}

export function toWireNamedInsured(row: NamedInsuredForm): NamedInsuredWritePayload {
    return {
        entity_name: emptyToNull(row.entityName),
        relationship: row.relationship === "" ? null : row.relationship,
        retroactive_date: formDateToIso(row.retroactiveDate),
    };
}

export interface AdditionalInsuredWritePayload {
    entity_name: string | null;
    coverage: AdditionalInsuredCoverage | null;
    retroactive_date: string | null;
}

export function toWireAdditionalInsured(row: AdditionalInsuredForm): AdditionalInsuredWritePayload {
    return {
        entity_name: emptyToNull(row.entityName),
        coverage: flagsToCoverage(row.gl, row.pl),
        retroactive_date: formDateToIso(row.retroactiveDate),
    };
}

/**
 * Location write payload (POST/PATCH /v2/submissions/{id}/locations).
 * Every key is optional so both the basic locations form
 * (`toWireLocation`) and the Facility-based Schedule table can produce
 * valid bodies. Mirrors `SubmissionLocationCreate`/`Update` in
 * orion-backend (PR #261).
 */
export interface LocationWritePayload {
    location_name?: string | null;
    /** Provider Number — the facility's CMS/CCN id. Real backend column
     *  (`cms_number` on SubmissionLocationCreate/Update); persisted on save. */
    cms_number?: string | null;
    acquired_date?: string | null;
    /** Operator name (`operator_name`). */
    operator_name?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    pl_retroactive_date?: string | null;
    gl_retroactive_date?: string | null;
    county?: string | null;
    urbanicity?: string | null;
    /** Retention / Self-Insured Retention (`sir`). */
    sir?: number | null;
    pl_limit?: number | null;
    gl_limit?: number | null;
    facility_id?: string | null;
    skilled_beds?: number | null;
    assisted_beds?: number | null;
    independent_beds?: number | null;
    additional_beds?: number | null;
    skilled_beds_licensed?: number | null;
    assisted_beds_licensed?: number | null;
    independent_beds_licensed?: number | null;
    memory_care_beds_licensed?: number | null;
    /** Maturity Credit as a fraction (1.0 = 100%), same unit as
     *  `sir_credit`. The FE dropdown works in whole-percent; the
     *  facilities endpoint converts at the boundary. */
    maturity_credit?: number | null;
    // ── FE-computed pricing values, persisted so the backend can drive
    //    its downstream quote calculations (Henry, jun-23). These mirror
    //    the read-only columns shown in the Facility Schedule and are
    //    derived by `domain/pricing/computeFacilityRow`; the FE owns the
    //    computation and sends the result on every location save.
    equivalency?: number | null;
    actuarial_rate?: number | null;
    unadjusted_premium?: number | null;
    maturity_adjusted_premium?: number | null;
    sir_credit?: number | null;
    sir_adjusted_premium?: number | null;
    /** Archive ("exclude") this location from pricing while keeping it on the
     *  submission — OR-1431. Accepted on both create and update. */
    is_excluded?: boolean;
}

export function toWireLocation(row: LocationForm): LocationWritePayload {
    return {
        location_name: emptyToNull(row.locationName),
        address: emptyToNull(row.address),
        city: emptyToNull(row.city),
        state: row.state === "" ? null : row.state,
        zip_code: emptyToNull(row.zip),
        pl_retroactive_date: formDateToIso(row.plRetroactiveDate),
        gl_retroactive_date: formDateToIso(row.glRetroactiveDate),
    };
}

export interface ContactWritePayload {
    risk_manager_name: string | null;
    risk_manager_email: string | null;
    risk_manager_phone: string | null;
    administrator_name: string | null;
    administrator_email: string | null;
    administrator_phone: string | null;
    administrator_duration_months: number | null;
}

export function toWireContact(form: GeminiContactsForm): ContactWritePayload {
    return {
        risk_manager_name: emptyToNull(form.riskManagerName),
        risk_manager_email: emptyToNull(form.riskManagerEmail),
        risk_manager_phone: emptyToNull(form.riskManagerPhone),
        administrator_name: emptyToNull(form.administratorName),
        administrator_email: emptyToNull(form.administratorEmail),
        administrator_phone: emptyToNull(form.administratorPhone),
        administrator_duration_months: durationFormToMonths(form),
    };
}

// ──────────────────────────── Relativity ──────────────────────

export interface RelativityWritePayload {
    skilled_rate: number;
    assisted_rate: number;
    independent_rate: number;
    additional_rate: number;
}

/** Form relativity row → `PUT /v2/submissions/{id}/relativity/` body.
 *  Empty / non-numeric cells collapse to 0 — the backend requires all
 *  four factors as numbers. */
export function toWireRelativity(row: RelativityRow): RelativityWritePayload {
    const num = (v: string): number => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };
    return {
        skilled_rate: num(row.skilled),
        assisted_rate: num(row.assisted),
        independent_rate: num(row.independent),
        additional_rate: num(row.additionalExposure),
    };
}
